import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { MatTooltip } from '@angular/material/tooltip';
import { toPng } from 'html-to-image';
import { download } from 'src/app/utils/file';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { CouponVip } from 'src/app/shared/models/dtos/responses/coupon-vip/coupon-vip';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { CouponStatus } from 'src/app/shared/models/constants/coupon-status';

@Component({
  selector: 'app-product-coupon-vip-content',
  templateUrl: './product-coupon-vip-content.component.html',
  styleUrls: ['./product-coupon-vip-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Organisms that shows the main content of Product Coupon page
 */ // eslint-disable-next-line
export class ProductCouponVipContentComponent implements OnInit {
  coupon!: CouponVip;
  title = '';
  label1 = 'product-coupon-vip-info-label-1';
  label2 = 'product-coupon-vip-info-label-2';
  translatedStrings = {
    'copy-coupon-code': 'copy-coupon-code',
    'download-coupon': 'download-coupon',
    print: 'print',
    codeCopied: 'code-copied',
    downloadSuccess: 'successfully-downloaded',
    downloadFail: 'error-downloading-coupon',
    printFail: 'error-printing-coupon',
    printSuccess: 'successfully-printed',
  };
  @ViewChild('buttons') buttonsArea?: ElementRef<HTMLDivElement>;
  @ViewChild('tooltip') tooltip?: MatTooltip;
  @ViewChild('couponCard') couponCardComponent?: ElementRef<HTMLElement>;
  loading: boolean = false;
  private readonly subsink = new Subscription();
  businessSummary?: BusinessSummaryResponseDTO;
  businessImgBase64: string = '';

  /**
   * @constructor
   */
  constructor(
    private clipboard: Clipboard,
    private translationService: TranslationService,
    private readonly snackBarService: SnackBarService,
    private readonly userService: UserService,
    private activatedRoute: ActivatedRoute
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.loading = true;
    this.translationService.translateObject(this.translatedStrings);
    const businessId = this.activatedRoute.snapshot.params['id'];
    const couponId = this.activatedRoute.snapshot.params['couponId'];
    this.subsink.add(
      this.userService.getCouponVipByBusinessId(businessId).subscribe({
        next: (res: [CouponVip]) => {
          for (const item of res) {
            if (item.id === parseInt(couponId)) {
              this.coupon = item;
              this.coupon.status = CouponStatus.ACTIVE;
              this.title = `${this.coupon.business.businessName} - ${this.coupon.vipName}`;
              break;
            }
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      })
    );
    this.subsink.add(
      this.userService.getBusinessSummary(businessId).subscribe((summary) => {
        this.businessSummary = summary;
        this.fetchBase64Img();
      })
    );
  }

  /**
   * Fetches the business image and converts it to base64
   */
  private async fetchBase64Img() {
    if (this.businessSummary?.picture) {
      this.businessImgBase64 = await this.convertUrlToBase64(
        this.businessSummary.picture
      );
    }
  }

  /**
   * @param {string} url
   * @return {Promise<string>} the base64 representation of the image
   */
  private async convertUrlToBase64(url: string): Promise<string> {
    const options: RequestInit = { responseType: 'blob' } as RequestInit;
    const response = await fetch(url, options);
    const blob = await response.blob();
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.addEventListener('error', (event) => reject(event));
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Copy text to clipboard
   */
  copy() {
    this.clipboard.copy(this.coupon?.code as string);
    this.snackBarService.success(this.translatedStrings.codeCopied);
  }

  /**
   * @param {number} familyId
   * @return {boolean} indicates if the timeline is disabled
   */
  isTimelineDisabled(familyId: number): boolean {
    for (const activeLevel of this.businessSummary!.activeBusinessLevels!) {
      if (activeLevel.familyPk == familyId) {
        return false;
      }
    }
    return true;
  }

  /**
   * Save a coupon as png
   */
  downloadCoupon() {
    if (this.couponCardComponent?.nativeElement) {
      const couponElement = this.couponCardComponent.nativeElement;
      couponElement.style.backgroundColor = 'white';
      toPng(couponElement)
        .then((couponCardPng) => {
          download(`coupon-${this.coupon?.code}.png`, couponCardPng);
          this.snackBarService.success(this.translatedStrings.downloadSuccess);
        })
        .catch(() => {
          this.snackBarService.error(this.translatedStrings.downloadFail);
        });
    }
  }

  /**
   * Saves a coupon as pdf
   */
  printCoupon() {
    if (this.couponCardComponent?.nativeElement) {
      toPng(this.couponCardComponent?.nativeElement)
        .then((couponCardPng) => {
          const w = window.open('', '_new');
          if (w) {
            const img = w.document.createElement('img');
            img.src = couponCardPng;
            img.onload = () => {
              w.document.body.appendChild(img);
              w.onafterprint = () => {
                w.close();
              };
              w.print();
            };
          }
          this.snackBarService.success(this.translatedStrings.printSuccess);
        })
        .catch(() => {
          this.snackBarService.error(this.translatedStrings.printFail);
        });
    }
  }
}
